// src/hooks/useUsers.js
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function useUsers() {
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState("");

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${API_BASE}/api/users`, { timeout: 30000, withCredentials: true });
            const data = res.data;
            const list = Array.isArray(data) ? data : Object.entries(data || {}).map(([username, u]) => ({ username, ...u }));
            const prepared = list.map((u, idx) => ({
                id: u.id ?? idx,
                username: u.username ?? "",
                firstname: u.firstname ?? "",
                lastname: u.lastname ?? "",
                name: [u.firstname, u.lastname].filter(Boolean).join(" ") || u.username || "",
                email: u.email ?? "",
                role: u.role ?? "",
                company: u.company ?? "",
                company_name: u.company_name ?? u.company ?? "",
                company_id: u.company_id ?? null,
                is_active: u.is_active ?? true,
                last_login_at: u.last_login_at ?? null,
            }));
            setUsers(prepared);
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.error || e?.message || "Konnte Nutzer nicht laden.");
        } finally {
            setLoading(false);
        }
    }, []);

    const createUser = useCallback(async (payload) => {
        const res = await axios.post(`${API_BASE}/api/users`, payload, {
            timeout: 30000, withCredentials: true, headers: { "Content-Type": "application/json" },
        });
        if (res.status >= 400) throw new Error(res?.data?.error || "Erstellen fehlgeschlagen.");
        await fetchUsers();
        return res?.data?.user || null;
    }, [fetchUsers]);

    const updateUser = useCallback(async (id, payload) => {
        const res = await axios.put(`${API_BASE}/api/users/${id}`, payload, {
            timeout: 30000, withCredentials: true, headers: { "Content-Type": "application/json" },
        });
        if (res.status >= 400) throw new Error(res?.data?.error || "Aktualisierung fehlgeschlagen.");
        await fetchUsers();
        return res?.data?.user || null;
    }, [fetchUsers]);

    const deleteUser = useCallback(async (id) => {
        const res = await axios.delete(`${API_BASE}/api/users/${id}`, {
            timeout: 30000, withCredentials: true
        });
        if (res.status >= 400) throw new Error(res?.data?.error || "Löschen fehlgeschlagen.");
        await fetchUsers();
        return true;
    }, [fetchUsers]);

    const getUserLoginHistory = useCallback(async (id) => {
        const res = await axios.get(`${API_BASE}/api/users/${id}/login-history`, {
            timeout: 30000,
            withCredentials: true,
        });
        return res.data;
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const roles = useMemo(
        () => Array.from(new Set(users.map((u) => u.role).filter(Boolean))),
        [users]
    );

    return { users, loading, error, fetchUsers, roles, createUser, updateUser, deleteUser, getUserLoginHistory };
}
